import React, { useState, useContext, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Divider, 
  Stack, 
  Alert, 
  Chip, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Tooltip,
  Badge
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CodeIcon from '@mui/icons-material/Code';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import Dashboard, { useDashboard } from '../../Layouts/Dashboard';
import { HttpRequestContext } from '../../Blocks/HttpRequestProvider';
import PDFViewer from '../../Components/PDFViewer';

// Define breadcrumbs for this page
const invoiceTemplateBreadcrumbs = [
  { href: '/', label: 'Accueil' },
  { href: '/invoicing', label: 'Facturation' },
  { label: 'Modèle de Facture' }
];

const InvoiceTemplateEditorContent = ({ setHeaderActions: propSetHeaderActions }) => {
  // Get setHeaderActions from context
  const { setHeaderActions: contextSetHeaderActions } = useDashboard();
  
  // Use the prop version if provided (backward compatibility) or the context version
  const setHeaderActions = propSetHeaderActions || contextSetHeaderActions;

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [pdfUrl, setPdfUrl] = useState('/invoicing/template');
  const [lastChangeDescription, setLastChangeDescription] = useState('');
  const [lastChangesList, setLastChangesList] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [sessionStatus, setSessionStatus] = useState({ is_saved: true, has_unsaved_changes: false, conversation_count: 0, saved_count: 0 });
  const [latexContent, setLatexContent] = useState('');
  const [showLatexDialog, setShowLatexDialog] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const { sendRequest } = useContext(HttpRequestContext);

  // Fetch initial template on component mount
  useEffect(() => {
    fetchInitialTemplate();
    fetchConversationHistory();
  }, []);

  // Function to refresh the PDF view by triggering a re-render
  const refreshPdfView = () => {
    // Force PDFViewer to refetch by creating a new object reference
    setPdfUrl('/invoicing/template');
  };

  const fetchInitialTemplate = async () => {
    try {
      // Also fetch the initial LaTeX content
      await fetchCurrentLatexTemplate(false);
    } catch (error) {
      console.error("Error fetching initial template:", error);
    }
  };

  const fetchConversationHistory = async (savedOnly = false) => {
    try {
      const response = await sendRequest({
        apiPath: `/invoicing/template/history${savedOnly ? '?saved_only=true' : ''}`,
        type: 'GET'
      });
      
      if (response) {
        if (response.history) {
          setConversationHistory(response.history);
          
          // Build message history from conversation data
          const messageHistory = [];
          response.history.forEach(item => {
            messageHistory.push({ sender: 'user', text: item.user_message });
            messageHistory.push({ sender: 'app', text: item.description });
          });
          
          setMessages(messageHistory);
          
          // If there's at least one entry, update the latest changes
          if (response.history.length > 0) {
            const latest = response.history[response.history.length - 1];
            setLastChangeDescription(latest.description);
            setLastChangesList(latest.changes || []);
            
            // Update the LaTeX content if available
            if (latest.updated_file) {
              setLatexContent(latest.updated_file);
            }
          }
        }
        
        if (response.status) {
          setSessionStatus(response.status);
        }
      }
    } catch (error) {
      console.error("Error fetching conversation history:", error);
    }
  };

  const saveCurrentSession = async () => {
    if (!sessionStatus.has_unsaved_changes) return;
    
    setIsSaving(true);
    try {
      const response = await sendRequest({
        apiPath: '/invoicing/template/save',
        type: 'POST'
      });
      
      if (response && response.status) {
        setSessionStatus(response.status);
      }
    } catch (error) {
      console.error("Error saving session:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const discardUnsavedChanges = async () => {
    if (!sessionStatus.has_unsaved_changes) return;
    
    setIsDiscarding(true);
    try {
      const response = await sendRequest({
        apiPath: '/invoicing/template/discard',
        type: 'POST'
      });
      
      if (response) {
        if (response.history) {
          setConversationHistory(response.history);
          
          // Build message history from conversation data
          const messageHistory = [];
          response.history.forEach(item => {
            messageHistory.push({ sender: 'user', text: item.user_message });
            messageHistory.push({ sender: 'app', text: item.description });
          });
          
          setMessages(messageHistory);
          
          // If there's at least one entry, update the latest changes
          if (response.history.length > 0) {
            const latest = response.history[response.history.length - 1];
            setLastChangeDescription(latest.description);
            setLastChangesList(latest.changes || []);
            
            // Update the LaTeX content if available
            if (latest.updated_file) {
              setLatexContent(latest.updated_file);
            }
          } else {
            // No history, reset UI
            setLastChangeDescription('');
            setLastChangesList([]);
          }
        }
        
        if (response.status) {
          setSessionStatus(response.status);
        }
        
        // Force PDF refresh
        refreshPdfView();
      }
    } catch (error) {
      console.error("Error discarding changes:", error);
    } finally {
      setIsDiscarding(false);
    }
  };

  const fetchCurrentLatexTemplate = async (showDialog = true) => {
    try {
      const response = await sendRequest({
        apiPath: '/invoicing/template/latex',
        type: 'GET'
      });
      
      if (response && response.template) {
        setLatexContent(response.template);
        if (showDialog) {
          setShowLatexDialog(true);
        }
      }
    } catch (error) {
      console.error("Error fetching LaTeX template:", error);
    }
  };

  const renderCustomLatex = async () => {
    if (!latexContent) return;
    
    setIsRendering(true);
    
    try {
      // Send the LaTeX to the server for rendering
      await sendRequest({
        apiPath: '/invoicing/template/render-latex',
        type: 'POST',
        request: { latex: latexContent }
      });
      
      // Force PDF refresh
      refreshPdfView();
      setShowLatexDialog(false);
    } catch (error) {
      console.error("Error rendering LaTeX:", error);
    } finally {
      setIsRendering(false);
    }
  };

  const handleCopyLatex = () => {
    navigator.clipboard.writeText(latexContent)
      .then(() => {
        alert("LaTeX content copied to clipboard!");
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };

  const handleNewChat = async () => {
    // If there are unsaved changes, confirm before resetting
    if (sessionStatus.has_unsaved_changes) {
      if (!window.confirm("Vous avez des modifications non enregistrées. Voulez-vous continuer et perdre ces modifications?")) {
        return;
      }
    }
    
    setMessages([]);
    setInputValue('');
    setLastChangeDescription('');
    setLastChangesList([]);
    setConversationHistory([]);
    
    try {
      // Reset to default template
      const response = await sendRequest({
        apiPath: '/invoicing/template/reset',
        type: 'POST'
      });
      
      if (response) {
        if (response.status) {
          setSessionStatus(response.status);
        }
        
        // Force PDF refresh
        refreshPdfView();
        
        // Also update the LaTeX content
        await fetchCurrentLatexTemplate(false);
      }
    } catch (error) {
      console.error("Error resetting template:", error);
    }
  };

  const processStructuredMessage = (messageText) => {
    // Handle specific patterns where we can directly specify changes
    // Example 1: "Qté -> Quantité"
    if (messageText.match(/Qt\u00e9\s*->\s*Quantit\u00e9/i)) {
      return {
        user_message: messageText,
        description: "Remplacement de l'en-tête 'Qté' par 'Quantité' dans le tableau de la facture.",
        changes: [
          {
            type: "replace",
            target: "Qté",
            replacement: "Quantité",
            location: "ligne d'en-tête du tableau de facture"
          }
        ]
      };
    }
    
    // Example 2: Change FACTURE to Facture de Vente
    if (messageText.match(/change.*facture.*facture de vente/i)) {
      return {
        user_message: messageText,
        description: "Remplacement du texte d'en-tête 'FACTURE' par 'Facture de Vente' dans la section d'en-tête de la facture.",
        changes: [
          {
            type: "replace",
            target: "{\\Huge \\textbf{FACTURE}}",
            replacement: "{\\Huge \\textbf{Facture de Vente}}",
            location: "section d'en-tête de la facture (minipage en haut à droite)"
          }
        ]
      };
    }
    
    // Default: return a basic structure and let the backend handle it
    return {
      user_message: messageText,
      description: "",
      changes: []
    };
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    // Add user's message to the chat history
    const userMessage = { sender: 'user', text: inputValue };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    
    // Show loading indicator in conversation
    const loadingMessage = { sender: 'app', text: 'Processing your request...' };
    setMessages(prev => [...prev, loadingMessage]);

    try {
      // Extract all user messages and create structured message objects
      const structuredMessages = [...messages, userMessage]
        .filter(msg => msg.sender === 'user')
        .map(msg => processStructuredMessage(msg.text));

      // Call the API to update the template using the structured conversation
      const response = await sendRequest({
        apiPath: '/invoicing/template/update-with-chat',
        type: 'POST',
        request: { structured_messages: structuredMessages },
      });
      
      // Remove the loading message
      setMessages(prev => prev.filter(msg => msg !== loadingMessage));
      
      if (response) {
        // Update conversation history if available
        if (response.conversation) {
          setConversationHistory(response.conversation);
          
          // Get the latest response
          const latestResponse = response.conversation[response.conversation.length - 1];
          
          if (latestResponse) {
            // Update latest changes info
            setLastChangeDescription(latestResponse.description);
            setLastChangesList(latestResponse.changes || []);
            
            // Update the LaTeX content
            if (latestResponse.updated_file) {
              setLatexContent(latestResponse.updated_file);
            }
            
            // Add app's response to the chat
            const appMessage = {
              sender: 'app',
              text: latestResponse.description
            };
            setMessages(prev => [...prev, appMessage]);
          }
        }
        
        // Update session status if available
        if (response.status) {
          setSessionStatus(response.status);
        }
        
        // Force PDF refresh
        refreshPdfView();
      }
    } catch (error) {
      // Remove the loading message
      setMessages(prev => prev.filter(msg => msg !== loadingMessage));
      
      console.error("Error updating template:", error);
      const errorMessage = {
        sender: 'app',
        text: 'Sorry, something went wrong while processing your request.'
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  // Helper function to get a readable change type
  const getChangeTypeLabel = (type) => {
    switch(type) {
      case 'replace': return 'Remplacé';
      case 'add': return 'Ajouté';
      case 'delete': return 'Supprimé';
      default: return 'Modifié';
    }
  };

  // Helper function to get a color for each change type
  const getChangeTypeColor = (type) => {
    switch(type) {
      case 'replace': return 'primary';
      case 'add': return 'success';
      case 'delete': return 'error';
      default: return 'default';
    }
  };

  const handleLatexChange = (e) => {
    setLatexContent(e.target.value);
  };

  // Set header actions (for dashboard navbar)
  useEffect(() => {
    setHeaderActions(
      <Box display="flex" gap={1}>
        <Tooltip title={sessionStatus.has_unsaved_changes ? "Enregistrer les modifications" : "Pas de modifications à enregistrer"}>
          <span>
            <Button 
              variant="outlined" 
              color="success" 
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={saveCurrentSession}
              disabled={!sessionStatus.has_unsaved_changes || isSaving}
              sx={{ mr: 1 }}
            >
              Enregistrer
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={sessionStatus.has_unsaved_changes ? "Annuler les modifications" : "Pas de modifications à annuler"}>
          <span>
            <Button 
              variant="outlined" 
              color="error" 
              startIcon={isDiscarding ? <CircularProgress size={20} color="inherit" /> : <UndoIcon />}
              onClick={discardUnsavedChanges}
              disabled={!sessionStatus.has_unsaved_changes || isDiscarding}
              sx={{ mr: 1 }}
            >
              Annuler
            </Button>
          </span>
        </Tooltip>
        <Button 
          variant="outlined" 
          color="secondary" 
          startIcon={<CodeIcon />}
          onClick={() => setShowLatexDialog(true)}
          sx={{ mr: 1 }}
        >
          Voir LaTeX
        </Button>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={handleNewChat}
        >
          Nouvelle Conversation
        </Button>
      </Box>
    );
    
    return () => setHeaderActions(null);
  }, [setHeaderActions, sessionStatus, isSaving, isDiscarding]);

  return (
    <Box p={2}>
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
        {/* Chat Section */}
        <Paper
          sx={{
            flex: 1,
            p: 2,
            maxHeight: '80vh',
            overflow: 'auto'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Discuter avec Notre Application
            </Typography>
            <Badge 
              color="warning" 
              variant="dot" 
              invisible={sessionStatus.is_saved}
              sx={{ '.MuiBadge-dot': { right: 10 } }}
            >
              <Typography variant="body2" color="text.secondary">
                {sessionStatus.is_saved ? 'Toutes les modifications sont enregistrées' : 'Modifications non enregistrées'}
              </Typography>
            </Badge>
          </Box>
          <Box mb={2} sx={{ maxHeight: 'calc(80vh - 140px)', overflow: 'auto' }}>
            {!sessionStatus.is_saved && (
              <Box mb={1}>
                <Paper
                  sx={{
                    display: 'inline-block',
                    p: 1,
                    bgcolor: 'warning.light',
                    width: '100%'
                  }}
                >
                  <Typography variant="body2">
                    Vous avez des modifications non enregistrées. Cliquez sur "Enregistrer" pour les conserver.
                  </Typography>
                </Paper>
              </Box>
            )}
            
            {messages.map((msg, index) => (
              <Box key={index} mb={1} textAlign={msg.sender === 'user' ? 'right' : 'left'}>
                <Paper
                  sx={{
                    display: 'inline-block',
                    p: 1,
                    bgcolor: msg.sender === 'user' ? 'primary.light' : 'grey.200',
                    maxWidth: '85%'
                  }}
                >
                  <Typography variant="body1">{msg.text}</Typography>
                </Paper>
                
                {/* Show technical details after AI responses */}
                {msg.sender === 'app' && index === messages.length - 1 && lastChangesList && lastChangesList.length > 0 && (
                  <Box mt={1} width="100%">
                    <Accordion 
                      sx={{ 
                        bgcolor: 'info.light', 
                        '&:before': { display: 'none' },
                        boxShadow: 'none'
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: '0 8px' }}>
                        <Typography variant="body2">Détails Techniques ({lastChangesList.length})</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {lastChangesList.map((change, index) => (
                            <Paper key={index} sx={{ p: 1 }}>
                              <Box display="flex" alignItems="center" mb={1}>
                                <Chip 
                                  label={getChangeTypeLabel(change.type)} 
                                  color={getChangeTypeColor(change.type)}
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                                <Typography variant="body2">
                                  {change.location || "Unknown location"}
                                </Typography>
                              </Box>
                              {change.type !== 'delete' && (
                                <Box>
                                  <Typography variant="caption" color="textSecondary">
                                    Change details:
                                  </Typography>
                                  <Box 
                                    component="pre"
                                    sx={{ 
                                      fontSize: '0.75rem',
                                      mt: 0.5,
                                      p: 1,
                                      bgcolor: 'rgba(0,0,0,0.04)',
                                      borderRadius: 1,
                                      overflow: 'auto',
                                      maxHeight: '100px'
                                    }}
                                  >
                                    {change.type === 'replace' ? 
                                      `${change.target} → ${change.replacement}` : 
                                      change.replacement
                                    }
                                  </Box>
                                </Box>
                              )}
                            </Paper>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
          <Divider />
          <Box mt={2} display="flex" gap={1}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Décrivez comment vous souhaitez modifier la facture (ex: 'Ajouter un logo', 'Changer les couleurs en bleu')"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
            />
            <Button variant="contained" color="primary" onClick={handleSend}>
              Envoyer
            </Button>
          </Box>
        </Paper>
        {/* PDF Preview Section */}
        <Paper
          sx={{
            flex: 1,
            overflow: 'auto',
            boxShadow: 'none',
            bgcolor: 'transparent',
            border: 'none'
          }}
        >
          <PDFViewer pdfUrl={pdfUrl} />
        </Paper>
      </Box>

      {/* LaTeX Content Dialog */}
      <Dialog
        open={showLatexDialog}
        onClose={() => setShowLatexDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Code du Modèle LaTeX</Typography>
            <Box>
              <IconButton 
                color="primary" 
                onClick={handleCopyLatex} 
                title="Copier dans le presse-papiers"
                sx={{ mr: 1 }}
              >
                <FileCopyIcon />
              </IconButton>
              <IconButton 
                onClick={() => setShowLatexDialog(false)}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            multiline
            fullWidth
            value={latexContent}
            onChange={handleLatexChange}
            variant="outlined"
            inputProps={{
              style: {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                lineHeight: 1.4,
              }
            }}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={isRendering ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
            onClick={renderCustomLatex}
            disabled={isRendering}
          >
            {isRendering ? 'Rendu en cours...' : 'Appliquer les Modifications'}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLatexDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Main component wrapped with Dashboard layout
const InvoiceTemplateEditor = () => {
  return (
    <Dashboard 
      dashboardTitle="Modèle de Facture - Chat et Aperçu PDF"
      breadcrumbs={invoiceTemplateBreadcrumbs}
    >
      <InvoiceTemplateEditorContent />
    </Dashboard>
  );
};

export default InvoiceTemplateEditor;
